/**
 * The Record, as one run sees it (D241) — what a run is handed at genesis,
 * what its `record_holds` conditions read, and what its endings write.
 *
 * @remarks
 * The engine never touches storage (`content-schema` `record.ts` has the
 * whole model). Four pure pieces, and every host reads the same four:
 *
 * - {@link recordForGenesis} — the Record a host hands in, made fit for this
 *   pack: only layers its `record.writtenBy` endings could have written,
 *   only the flags its `record.remembers` names, folded and capped. A pack
 *   that declares no `record` begins every run with none, whatever it is
 *   handed. A golden replay, `runEveryRoad` and every test that hands
 *   nothing in begin with an empty Record, so they stay pure unchanged.
 * - {@link isRecordHolding} — the `record_holds` condition, over the view the
 *   run began with and nothing written since.
 * - {@link writeRecordLayers} — the turn's step 7c (`complete-turn.ts`):
 *   every ending this turn selected that the pack lists in `writtenBy`
 *   folds its layer into the run's `writtenThisRun`, its remembered flags
 *   read as the turn left them — BEFORE a checkpoint return puts the world
 *   back, so a death remembers where the player died.
 * - {@link recordedLayersOf} and {@link nextRecordOf} — what a host reads
 *   back: the layers one turn wrote, for it to keep; and the Record the next
 *   run should begin on, which the in-game `restart` uses too.
 *
 * @packageDocumentation
 */

import {
	type ConditionDefinition,
	type EndingDefinition,
	type FlagId,
	mergeRecordLayers,
	type RecordHoldsCondition,
	type RecordLayer,
	recordLayerKeyOf,
	type RecordLayerKind,
	type RuntimeState,
	sortedRemembered,
} from '@orpheus/content-schema';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

/** The run's view at genesis when nothing is remembered. */
export const EMPTY_RECORD_VIEW: RuntimeState['record'] = { heldAtGenesis: [], writtenThisRun: []};

/**
 * Whether an ending is a DEATH for the Record's purposes (Calder's ruling,
 * §13.16): it returns the player to a checkpoint, so the run goes on.
 *
 * @param ending - The ending
 * @returns `death` for a checkpoint return, `ending` for everything else
 */
export const recordLayerKindOf = (ending: EndingDefinition): RecordLayerKind => (ending.restartAt === 'checkpoint' ? 'death' : 'ending');

/**
 * The Record a host hands in, made fit for this pack (see the module
 * remarks). Untrusted input is expected — a device's storage, an older
 * version of the pack — so nothing here throws: what does not belong is
 * left out.
 *
 * @param bundle - The loaded bundle
 * @param handed - The layers the host read, if any
 * @returns The Record this run begins on
 */
export const recordForGenesis = (bundle: EngineGameBundle, handed: readonly RecordLayer[] = []): readonly RecordLayer[] => {
	const declaration = bundle.contentPack.record;

	if (declaration === undefined) {
		return [];
	}

	const remembers = new Set<string>(declaration.remembers);
	const fitting = handed
		.filter((layer) => declaration.writtenBy.includes(layer.endingId))
		.map((layer) => ({ ...layer, remembered: Object.fromEntries(Object.entries(layer.remembered).filter(([flagId]) => remembers.has(flagId))) }));

	return mergeRecordLayers([], fitting);
};

/**
 * `record_holds` (D241): do at least `minimumCount` of the layers this run
 * BEGAN with match — by ending, by kind, by their remembered flags?
 *
 * @param condition - The condition
 * @param bundle - The loaded bundle (an ending's kind, a flag's declared start)
 * @param state - The live runtime state
 * @returns Whether the Record holds enough matching layers
 */
export const isRecordHolding = (condition: RecordHoldsCondition, bundle: EngineGameBundle, state: RuntimeState): boolean => {
	const matchingCount = state.record.heldAtGenesis
		.filter((layer) => condition.endingId === undefined || layer.endingId === condition.endingId)
		.filter((layer) => condition.kind === undefined || layerKindOf(bundle, layer) === condition.kind)
		.filter((layer) => condition.where === undefined || holdsOverRemembered(condition.where, bundle, layer.remembered))
		.reduce((sum, layer) => sum + layer.count, 0);

	return matchingCount >= (condition.minimumCount ?? 1);
};

/**
 * A layer's kind, read off the ending that wrote it. A layer always names a
 * `writtenBy` ending ({@link recordForGenesis} keeps no other), and the
 * loader refuses a `writtenBy` naming an undeclared ending, so the lookup
 * cannot miss on a loaded pack; a miss would read as neither kind.
 *
 * @param bundle - The loaded bundle
 * @param layer - The layer
 * @returns Its kind
 */
const layerKindOf = (bundle: EngineGameBundle, layer: RecordLayer): RecordLayerKind | undefined => {
	const ending = bundle.indexes.endingsById.get(layer.endingId);

	return ending === undefined ? undefined : recordLayerKindOf(ending);
};

/**
 * A `where` over one layer's remembered flags — the narrowed grammar the
 * loader allows there: `flag_equals`, `all_of`, `any_of`, `at_least_of`,
 * `not`. A flag the layer does not carry (the pack began remembering it
 * after the layer was written) reads as its declared starting value: the run
 * that wrote the layer never said otherwise. Any other leaf reads false —
 * inside a layer there is no world to ask, and the loader has refused it.
 *
 * @param condition - The `where`
 * @param bundle - The loaded bundle (a flag's declared start)
 * @param remembered - The layer's flags
 * @returns Whether it holds of that run
 */
const holdsOverRemembered = (condition: ConditionDefinition, bundle: EngineGameBundle, remembered: Readonly<Record<string, boolean>>): boolean => {
	switch (condition.type) {
		case 'all_of': {
			return condition.conditions.every((member) => holdsOverRemembered(member, bundle, remembered));
		}
		case 'any_of': {
			return condition.conditions.some((member) => holdsOverRemembered(member, bundle, remembered));
		}
		case 'at_least_of': {
			return condition.conditions.filter((member) => holdsOverRemembered(member, bundle, remembered)).length >= condition.minimumCount;
		}
		case 'flag_equals': {
			return (remembered[condition.flagId] ?? bundle.indexes.flagDefinitionsById.get(condition.flagId)?.initialValue) === condition.expectedValue;
		}
		case 'not': {
			return !holdsOverRemembered(condition.condition, bundle, remembered);
		}
		default: {
			return false;
		}
	}
};

/**
 * Step 7c of the turn (D241): fold a layer into the run's `writtenThisRun`
 * for every ending this turn selected that the pack's `record.writtenBy`
 * lists — its remembered flags read from the world as the turn left it.
 *
 * @param bundle - The loaded bundle
 * @param before - The state the turn began from
 * @param after - The state after the triggers and the late tick, before any checkpoint return
 * @returns `after`, with the layers written
 */
export const writeRecordLayers = (bundle: EngineGameBundle, before: RuntimeState, after: RuntimeState): RuntimeState => {
	const declaration = bundle.contentPack.record;

	if (declaration === undefined) {
		return after;
	}

	const writing = after.narrative.selectedEndingIds.slice(before.narrative.selectedEndingIds.length).filter((endingId) => declaration.writtenBy.includes(endingId));

	if (writing.length === 0) {
		return after;
	}

	const remembered = sortedRemembered(Object.fromEntries(declaration.remembers.map((flagId): readonly [FlagId, boolean] => [flagId, after.world.flagValues[flagId] === true])));
	const written = writing.map((endingId): RecordLayer => ({ count: 1, endingId, remembered }));

	return { ...after, record: { ...after.record, writtenThisRun: mergeRecordLayers(after.record.writtenThisRun, written) }};
};

/**
 * The layers one turn wrote (D241) — what a host keeps, after the turn
 * commits: each layer whose count grew, by how much it grew.
 *
 * @param before - The state before the turn
 * @param after - The committed state after it
 * @returns The new layers, in the order written; empty for nearly every turn
 */
export const recordedLayersOf = (before: RuntimeState, after: RuntimeState): readonly RecordLayer[] => {
	const countBefore = new Map(before.record.writtenThisRun.map((layer) => [recordLayerKeyOf(layer), layer.count]));

	return after.record.writtenThisRun.flatMap((layer) => {
		const grownBy = layer.count - (countBefore.get(recordLayerKeyOf(layer)) ?? 0);

		return grownBy > 0 ? [{ ...layer, count: grownBy }] : [];
	});
};

/**
 * The Record the NEXT run should begin on (D241): what this run began with,
 * and what it has written since, folded — the in-game `restart`'s genesis,
 * and a host's answer when it has nothing better.
 *
 * @param state - The live runtime state
 * @returns The Record after this run
 */
export const nextRecordOf = (state: RuntimeState): readonly RecordLayer[] => mergeRecordLayers(state.record.heldAtGenesis, state.record.writtenThisRun);
